import React, { useState, useEffect, useMemo } from "react";
import { dashboardAPI, customersAPI, formatKSH } from "../services/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import DataTable from "../components/DataTable";

/* ── helpers ──────────────────────────────────────────────────────── */
const fmtLarge = n => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};
const fmtKSHShort = a => `KSH ${fmtLarge(a)}`;
const fmtDate     = d => { if (!d) return 'Never'; return new Date(d).toLocaleDateString('en-KE'); };

const COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4'];

const REPORT_TABS = [
  { id: 'overview',      name: 'Overview'           },
  { id: 'customers',     name: 'Customer Report'    },
  { id: 'interactions',  name: 'Interaction Trends' },
  { id: 'revenue',       name: 'Revenue Analysis'   },
  { id: 'performance',   name: 'Performance Metrics'},
];

/* ── component ────────────────────────────────────────────────────── */
const Reports = () => {
  const [stats, setStats]                   = useState(null);
  const [allCustomers, setAllCustomers]     = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState("");
  const [activeReport, setActiveReport]     = useState("overview");
  const [customerFilter, setCustomerFilter] = useState("all");
  // filter ids: all | not_visited | not_ordered | needs_followup | no_recent_orders | at_risk | active | no_email

  /* ── fetch ────────────────────────────────────────────────────────── */
  const fetchReportData = async () => {
    try {
      setLoading(true);
      const [statsRes, customersRes] = await Promise.all([
        dashboardAPI.getStats(),
        customersAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setAllCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
    } catch (err) {
      setError("Failed to load report data");
      console.error("Reports error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReportData(); }, []);

  /* ── customer report filter logic ─────────────────────────────────── */
  const customerSets = useMemo(() => {
    const flag = c => ({
      hasContact:    parseInt(c.interaction_count || 0) > 0,
      hasOrder:      parseInt(c.order_count || 0) > 0,
      followupFlag:  c.needs_followup === 1 || c.needs_followup === true,
      orderFlag:     c.needs_order    === 1 || c.needs_order    === true,
      missingEmail:  !c.email || String(c.email).trim() === '',
    });

    const notVisited       = allCustomers.filter(c => !flag(c).hasContact);
    const neverOrdered     = allCustomers.filter(c => !flag(c).hasOrder);
    const needsFollowup    = allCustomers.filter(c => flag(c).followupFlag && flag(c).hasContact);
    const noRecentOrders   = allCustomers.filter(c => flag(c).orderFlag    && flag(c).hasOrder);
    const atRisk           = allCustomers.filter(c => flag(c).followupFlag && flag(c).orderFlag);
    const active           = allCustomers.filter(c => !flag(c).followupFlag && !flag(c).orderFlag && flag(c).hasContact && flag(c).hasOrder);
    const noEmail          = allCustomers.filter(c => flag(c).missingEmail);

    return { notVisited, neverOrdered, needsFollowup, noRecentOrders, atRisk, active, noEmail };
  }, [allCustomers]);

  const customerReportData = useMemo(() => {
    const map = {
      not_visited:      customerSets.notVisited,
      not_ordered:      customerSets.neverOrdered,
      needs_followup:   customerSets.needsFollowup,
      no_recent_orders: customerSets.noRecentOrders,
      at_risk:          customerSets.atRisk,
      active:           customerSets.active,
      no_email:         customerSets.noEmail,
    };
    return map[customerFilter] ?? allCustomers;
  }, [customerFilter, allCustomers, customerSets]);

  /* ── customer report columns ──────────────────────────────────────── */
  const customerColumns = [
    {
      key: '_no', label: '#',
      sortable: false, export: false,
      thClass: 'w-12',
      render: (_, __, idx) => (
        <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>
      ),
    },
    {
      key: 'customer_name', label: 'Customer Name',
      exportValue: r => r.customer_name,
      render: val => <span className="font-medium">{val}</span>,
    },
    {
      key: 'contact_number', label: 'Phone',
      exportValue: r => r.contact_number || r.mobile_number || '',
      render: (_, row) => row.contact_number || row.mobile_number || '-',
    },
    {
      key: 'county', label: 'County',
      exportValue: r => r.county || '',
      render: val => val || <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: 'last_contact_date', label: 'Last Contact',
      exportValue: r => fmtDate(r.last_contact_date),
      render: val => val
        ? <span className="text-warning-600 font-medium">{fmtDate(val)}</span>
        : <span className="badge badge-warning text-xs">Never Contacted</span>,
    },
    {
      key: 'last_order_date', label: 'Last Order',
      exportValue: r => fmtDate(r.last_order_date),
      render: val => val
        ? <span className="text-danger-600 font-medium">{fmtDate(val)}</span>
        : <span className="badge badge-danger text-xs">Never Ordered</span>,
    },
    {
      key: 'interaction_count', label: 'Visits',
      exportValue: r => r.interaction_count ?? 0,
      render: val => (
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
          parseInt(val) === 0 ? 'bg-warning-50 text-warning-700' : 'bg-primary-50 text-primary-700'
        }`}>
          {val ?? 0}
        </span>
      ),
    },
    {
      key: 'order_count', label: 'Orders',
      exportValue: r => r.order_count ?? 0,
      render: val => (
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
          parseInt(val) === 0 ? 'bg-red-50 text-red-700' : 'bg-success-50 text-success-700'
        }`}>
          {val ?? 0}
        </span>
      ),
    },
    {
      key: 'status_info', label: 'Status',
      sortable: false,
      exportValue: r => {
        const tags = [];
        if (!parseInt(r.interaction_count || 0))             tags.push('Not Visited');
        else if (r.needs_followup === 1 || r.needs_followup === true) tags.push('Needs Follow-up');
        if (!parseInt(r.order_count || 0))                   tags.push('Never Ordered');
        else if (r.needs_order === 1 || r.needs_order === true) tags.push('No Recent Orders');
        if (!r.email || !String(r.email).trim())             tags.push('No Email');
        return tags.length > 0 ? tags.join(' | ') : 'Active';
      },
      render: (_, row) => {
        const neverContacted = parseInt(row.interaction_count || 0) === 0;
        const neverOrdered   = parseInt(row.order_count || 0) === 0;
        const followup       = row.needs_followup === 1 || row.needs_followup === true;
        const noRecent       = row.needs_order    === 1 || row.needs_order    === true;
        const missingEmail   = !row.email || String(row.email).trim() === '';

        const badges = [];
        if (neverContacted)   badges.push(<span key="nv"  className="badge badge-gray   text-xs">Not Visited</span>);
        else if (followup)    badges.push(<span key="fu"  className="badge badge-warning text-xs">Follow-up Due</span>);
        if (neverOrdered)     badges.push(<span key="no"  className="badge badge-danger  text-xs">Never Ordered</span>);
        else if (noRecent)    badges.push(<span key="nr"  className="badge badge-danger  text-xs">Order Overdue</span>);
        if (missingEmail)     badges.push(<span key="me"  className="badge badge-gray    text-xs">No Email</span>);

        if (badges.length === 0)
          return <span className="badge badge-success text-xs">Active</span>;

        return <div className="flex flex-wrap gap-1">{badges}</div>;
      },
    },
  ];

  const customerSearchFields = ['customer_name', 'contact_number', 'mobile_number', 'county'];

  /* ── export helpers for overview (CSV style) ──────────────────────── */
  const exportOverviewCSV = () => {
    if (!stats) return;
    const rows = [
      ['Metric', 'Value'],
      ['Total Customers',            stats.total_customers      ?? 0],
      ['Interactions This Month',    stats.interactions_this_month ?? 0],
      ['Orders This Month',          stats.orders_this_month    ?? 0],
      ['Revenue This Month (KSH)',   stats.revenue_this_month   ?? 0],
      ['Customers Need Follow-up',   stats.customers_need_followup ?? 0],
      ['Customers No Recent Orders', stats.customers_no_recent_orders ?? 0],
    ];
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${activeReport}-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printOverview = () => {
    const el = document.getElementById('report-content');
    if (!el) return;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head>
      <title>Customer Report System – ${activeReport}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:20px;font-size:13px}
        h1,h2{color:#1e3a5f} .grid{display:flex;flex-wrap:wrap;gap:16px}
        .card{border:1px solid #ddd;padding:16px;border-radius:6px;min-width:180px}
        .num{font-size:28px;font-weight:bold;color:#2563eb}
        table{width:100%;border-collapse:collapse;margin:16px 0}
        th,td{border:1px solid #ddd;padding:8px;text-align:left;font-size:12px}
        th{background:#f5f5f5}
        @media print{body{margin:0}@page{margin:1cm}}
      </style>
    </head><body>
      <h1>Customer Report System</h1>
      <h2>${activeReport.charAt(0).toUpperCase() + activeReport.slice(1)} Report</h2>
      <p style="color:#666;font-size:12px">Generated: ${new Date().toLocaleString()}</p>
      ${el.innerHTML}
    </body></html>`);
    win.document.close(); win.focus();
    setTimeout(() => win.print(), 600);
  };

  /* ── loading / error ──────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1,2,3].map(i => (
            <div key={i} className="card">
              <div className="card-body space-y-4">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-32 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={fetchReportData} className="btn btn-primary">Retry</button>
      </div>
    );
  }

  /* ── render ───────────────────────────────────────────────────────── */
  return (
    <div className="space-y-6 print:space-y-4">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports &amp; Analytics</h1>
          <p className="mt-1 text-sm text-gray-600">
            Comprehensive insights into your customer management performance
          </p>
        </div>
        {activeReport !== 'customers' && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={exportOverviewCSV} className="btn btn-secondary inline-flex items-center gap-2 text-sm">
              <svg className="w-4 h-4 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
            <button onClick={printOverview} className="btn btn-primary inline-flex items-center gap-2 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H9.414a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 005.586 6H4a2 2 0 00-2 2v6a2 2 0 002 2h2m3 4h6m-6 0l3-3m-3 3l3 3" />
              </svg>
              Print PDF
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 print:hidden">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          {REPORT_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveReport(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
                activeReport === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Tab Content ─────────────────────────────────────────────── */}
      <div id="report-content" className="space-y-6">

        {/* ── OVERVIEW ─────────────────────────────────────────────── */}
        {activeReport === 'overview' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 print:block hidden">Business Overview</h2>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total Customers',          val: stats?.total_customers ?? 0,           color: 'text-primary-600' },
                { label: 'Interactions This Month',  val: stats?.interactions_this_month ?? 0,   color: 'text-success-600' },
                { label: 'Orders This Month',        val: stats?.orders_this_month ?? 0,         color: 'text-warning-600' },
                { label: 'Revenue This Month',       val: fmtKSHShort(stats?.revenue_this_month ?? 0), color: 'text-purple-600', small: true },
              ].map((m, i) => (
                <div key={i} className="card">
                  <div className="card-body text-center py-5">
                    <div className={`text-3xl font-bold ${m.color} ${m.small ? 'text-xl' : ''}`}>{m.val}</div>
                    <div className="text-sm text-gray-600 mt-1">{m.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Attention Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card border-l-4 border-l-warning-400">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">Needs Follow-up</h3>
                </div>
                <div className="card-body">
                  <div className="text-4xl font-bold text-warning-600 mb-2">
                    {stats?.customers_need_followup ?? 0}
                  </div>
                  <p className="text-sm text-gray-600">Customers not contacted in 14+ days</p>
                </div>
              </div>
              <div className="card border-l-4 border-l-danger-400">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">No Recent Orders</h3>
                </div>
                <div className="card-body">
                  <div className="text-4xl font-bold text-danger-600 mb-2">
                    {stats?.customers_no_recent_orders ?? 0}
                  </div>
                  <p className="text-sm text-gray-600">Customers without orders in 30+ days</p>
                </div>
              </div>
            </div>

            {/* Top Customers by Revenue */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Top Customers by Revenue</h3>
              </div>
              <div className="card-body">
                <DataTable
                  data={stats?.top_customers || []}
                  columns={[
                    {
                      key: '_no', label: '#',
                      sortable: false, export: false,
                      thClass: 'w-12',
                      render: (_, __, idx) => (
                        <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>
                      ),
                    },
                    {
                      key: 'customer_name', label: 'Customer',
                      exportValue: r => r.customer_name,
                      render: val => <span className="font-medium text-gray-900">{val}</span>,
                    },
                    {
                      key: 'order_count', label: 'Orders',
                      exportValue: r => r.order_count ?? 0,
                      render: val => (
                        <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-primary-50 text-primary-700 text-xs font-semibold">
                          {val ?? 0}
                        </span>
                      ),
                    },
                    {
                      key: 'total_orders', label: 'Total Revenue (KSH)',
                      exportValue: r => parseFloat(r.total_orders || 0).toFixed(2),
                      render: val => (
                        <span className="font-semibold text-success-600">{formatKSH(val || 0)}</span>
                      ),
                    },
                    {
                      key: 'last_order_date', label: 'Last Order',
                      exportValue: r => fmtDate(r.last_order_date),
                      render: val => <span className="text-sm text-gray-600">{fmtDate(val)}</span>,
                    },
                  ]}
                  searchFields={['customer_name']}
                  filename="top-customers-by-revenue"
                  title="Top Customers by Revenue"
                  loading={loading}
                  rowKey={row => row.customer_name}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── CUSTOMER REPORT ──────────────────────────────────────── */}
        {activeReport === 'customers' && (
          <div className="space-y-6">

            {/* ── Filter cards grid ─────────────────────────────────── */}
            {(() => {
              const FILTERS = [
                {
                  id:     'all',
                  label:  'All Customers',
                  desc:   'Complete customer list',
                  count:  allCustomers.length,
                  color:  'text-primary-700',
                  bg:     'bg-primary-50',
                  border: 'border-primary-400',
                  dot:    'bg-primary-500',
                },
                {
                  id:     'not_visited',
                  label:  'Not Visited',
                  desc:   'Never been contacted',
                  count:  customerSets.notVisited.length,
                  color:  'text-gray-700',
                  bg:     'bg-gray-50',
                  border: 'border-gray-400',
                  dot:    'bg-gray-500',
                },
                {
                  id:     'not_ordered',
                  label:  'Never Ordered',
                  desc:   'Zero purchase history',
                  count:  customerSets.neverOrdered.length,
                  color:  'text-orange-700',
                  bg:     'bg-orange-50',
                  border: 'border-orange-400',
                  dot:    'bg-orange-500',
                },
                {
                  id:     'needs_followup',
                  label:  'Needs Follow-up',
                  desc:   'Contacted before, now 14+ days silent',
                  count:  customerSets.needsFollowup.length,
                  color:  'text-warning-700',
                  bg:     'bg-warning-50',
                  border: 'border-warning-400',
                  dot:    'bg-warning-500',
                },
                {
                  id:     'no_recent_orders',
                  label:  'No Recent Orders',
                  desc:   'Ordered before, silent 30+ days',
                  count:  customerSets.noRecentOrders.length,
                  color:  'text-amber-700',
                  bg:     'bg-amber-50',
                  border: 'border-amber-400',
                  dot:    'bg-amber-500',
                },
                {
                  id:     'at_risk',
                  label:  'At Risk',
                  desc:   'Overdue for both contact & orders',
                  count:  customerSets.atRisk.length,
                  color:  'text-danger-700',
                  bg:     'bg-red-50',
                  border: 'border-danger-400',
                  dot:    'bg-danger-500',
                },
                {
                  id:     'active',
                  label:  'Active',
                  desc:   'Healthy - recent contact & orders',
                  count:  customerSets.active.length,
                  color:  'text-success-700',
                  bg:     'bg-success-50',
                  border: 'border-success-400',
                  dot:    'bg-success-500',
                },
                {
                  id:     'no_email',
                  label:  'No Email',
                  desc:   'Missing email address',
                  count:  customerSets.noEmail.length,
                  color:  'text-purple-700',
                  bg:     'bg-purple-50',
                  border: 'border-purple-400',
                  dot:    'bg-purple-500',
                },
              ];

              return (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {FILTERS.map(f => (
                    <button
                      key={f.id}
                      onClick={() => setCustomerFilter(f.id)}
                      title={f.desc}
                      className={`rounded-xl border-2 p-3 text-left transition-all duration-150 hover:shadow-md focus:outline-none ${
                        customerFilter === f.id
                          ? `${f.border} shadow-md ${f.bg}`
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-end mb-1">
                        {customerFilter === f.id && (
                          <span className={`w-2 h-2 rounded-full ${f.dot}`} />
                        )}
                      </div>
                      <div className={`text-2xl font-bold leading-none mt-1 ${f.color}`}>
                        {f.count}
                      </div>
                      <div className="text-xs font-semibold text-gray-700 mt-1 leading-tight">
                        {f.label}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5 leading-tight hidden sm:block">
                        {f.desc}
                      </div>
                    </button>
                  ))}
                </div>
              );
            })()}

            {/* ── Description banner ────────────────────────────────── */}
            {customerFilter !== 'all' && (() => {
              const INFO = {
                not_visited:      { bg: 'bg-gray-50',     border: 'border-gray-300',   text: 'text-gray-800',   label: 'Not Visited',       body: 'Customers who have never been contacted. Assign them to a salesperson and schedule a first visit.' },
                not_ordered:      { bg: 'bg-orange-50',   border: 'border-orange-300', text: 'text-orange-800', label: 'Never Ordered',     body: 'Customers who have never placed an order. Investigate barriers and offer introductory promotions.' },
                needs_followup:   { bg: 'bg-warning-50',  border: 'border-warning-300',text: 'text-warning-800',label: 'Needs Follow-up',   body: 'Customers previously contacted but not reached in 14+ days. Prioritise re-engagement calls or visits.' },
                no_recent_orders: { bg: 'bg-amber-50',    border: 'border-amber-300',  text: 'text-amber-800',  label: 'No Recent Orders',  body: 'Customers who have ordered before but placed no order in the last 30+ days. Reach out with restocking reminders.' },
                at_risk:          { bg: 'bg-red-50',      border: 'border-red-300',    text: 'text-red-800',    label: 'At Risk',           body: 'Customers overdue for both a visit AND a new order — the highest-priority segment for immediate action.' },
                active:           { bg: 'bg-success-50',  border: 'border-success-300',text: 'text-success-800',label: 'Active',            body: 'Customers with healthy, recent contact and order history. Maintain frequency and look for upsell opportunities.' },
                no_email:         { bg: 'bg-purple-50',   border: 'border-purple-300', text: 'text-purple-800', label: 'No Email',          body: 'Customers missing an email address. Update records to enable email campaigns and digital communication.' },
              };
              const i = INFO[customerFilter];
              if (!i) return null;
              return (
                <div className={`${i.bg} border ${i.border} rounded-lg px-4 py-3 text-sm ${i.text} flex items-start gap-2`}>
                  <span className="font-bold shrink-0">{i.label}:</span>
                  <span>{i.body}</span>
                </div>
              );
            })()}

            {/* ── DataTable ─────────────────────────────────────────── */}
            <div className="card">
              <div className="card-header flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-lg font-semibold text-gray-900">
                  {({
                    all:              'All Customers',
                    not_visited:      'Customers Not Visited',
                    not_ordered:      'Customers Who Never Ordered',
                    needs_followup:   'Customers Needing Follow-up',
                    no_recent_orders: 'Customers With No Recent Orders',
                    at_risk:          'At-Risk Customers',
                    active:           'Active Customers',
                    no_email:         'Customers Missing Email',
                  })[customerFilter] ?? 'Customers'}
                </h3>
                <span className="badge badge-info text-xs">
                  {customerReportData.length} records
                </span>
              </div>
              <div className="card-body">
                <DataTable
                  key={customerFilter}
                  data={customerReportData}
                  columns={customerColumns}
                  searchFields={customerSearchFields}
                  filename={`customer-report-${customerFilter}`}
                  title={({
                    all:              'All Customers Report',
                    not_visited:      'Customers Not Visited Report',
                    not_ordered:      'Customers Who Never Ordered Report',
                    needs_followup:   'Customers Needing Follow-up Report',
                    no_recent_orders: 'Customers With No Recent Orders Report',
                    at_risk:          'At-Risk Customers Report',
                    active:           'Active Customers Report',
                    no_email:         'Customers Missing Email Report',
                  })[customerFilter] ?? 'Customer Report'}
                  loading={false}
                  rowKey={row => row.id}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── INTERACTION TRENDS ───────────────────────────────────── */}
        {activeReport === 'interactions' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Trend chart */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">Interaction Trends (Last 6 Months)</h3>
                </div>
                <div className="card-body">
                  {stats?.interaction_trends?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={stats.interaction_trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-48 text-gray-400">
                      No interaction trend data available
                    </div>
                  )}
                </div>
              </div>

              {/* Contact method distribution */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">Contact Method Distribution</h3>
                </div>
                <div className="card-body">
                  {stats?.contact_method_distribution?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={stats.contact_method_distribution}
                          cx="50%" cy="50%" outerRadius={90}
                          dataKey="count"
                          label={({ contact_method, percent }) =>
                            `${contact_method} ${(percent * 100).toFixed(0)}%`
                          }
                        >
                          {stats.contact_method_distribution.map((_, i) => (
                            <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-48 text-gray-400">
                      No contact method data
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Monthly stats */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Monthly Interaction Breakdown</h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'This Month',      val: stats?.interactions_this_month ?? 0, color: 'text-blue-600',   bg: 'bg-blue-50' },
                    { label: 'Avg / Customer',  val: stats?.total_customers > 0 ? Math.round(((stats?.interactions_this_month ?? 0) / stats.total_customers) * 100) / 100 : 0, color: 'text-green-600', bg: 'bg-green-50' },
                    { label: 'Last 6 Months',   val: (stats?.interaction_trends || []).reduce((t, x) => t + (x.count || 0), 0), color: 'text-purple-600', bg: 'bg-purple-50' },
                    { label: 'Monthly Average', val: stats?.interaction_trends?.length > 0 ? Math.round((stats.interaction_trends.reduce((t, x) => t + (x.count || 0), 0)) / stats.interaction_trends.length) : 0, color: 'text-orange-600', bg: 'bg-orange-50' },
                  ].map((m, i) => (
                    <div key={i} className={`${m.bg} rounded-lg p-4 text-center`}>
                      <div className={`text-2xl font-bold ${m.color}`}>{m.val}</div>
                      <div className="text-sm text-gray-600 mt-1">{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── REVENUE ANALYSIS ─────────────────────────────────────── */}
        {activeReport === 'revenue' && (
          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Revenue Trends (Last 6 Months)</h3>
              </div>
              <div className="card-body">
                {stats?.order_trends?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={stats.order_trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={v => fmtKSHShort(v)} />
                      <Tooltip formatter={v => [formatKSH(v), 'Revenue']} />
                      <Bar dataKey="revenue" fill="#10b981" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-48 text-gray-400">
                    No revenue trend data
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">Revenue Summary</h3>
                </div>
                <div className="card-body space-y-4">
                  <div className="text-center p-5 bg-success-50 rounded-lg">
                    <div className="text-3xl font-bold text-success-600">
                      {fmtKSHShort(stats?.revenue_this_month ?? 0)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">This Month</div>
                  </div>
                  <div className="text-center p-5 bg-primary-50 rounded-lg">
                    <div className="text-2xl font-bold text-primary-600">
                      {formatKSH((stats?.order_trends || []).reduce((t, x) => t + parseFloat(x.revenue || 0), 0))}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Last 6 Months Total</div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">Monthly Breakdown</h3>
                </div>
                <div className="card-body p-0">
                  <div className="divide-y divide-gray-200 max-h-72 overflow-y-auto custom-scrollbar">
                    {[...(stats?.order_trends || [])].reverse().map((t, i) => (
                      <div key={i} className="px-4 py-3 flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm">{t.month}</div>
                          <div className="text-xs text-gray-500">{t.count} orders</div>
                        </div>
                        <div className="font-semibold text-success-600 text-sm">
                          {fmtKSHShort(t.revenue || 0)}
                        </div>
                      </div>
                    ))}
                    {!stats?.order_trends?.length && (
                      <div className="px-4 py-8 text-center text-gray-400 text-sm">No data available</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── PERFORMANCE METRICS ──────────────────────────────────── */}
        {activeReport === 'performance' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  label: 'Avg Interactions / Customer',
                  val: stats?.total_customers > 0
                    ? Math.round(((stats?.interactions_this_month ?? 0) / stats.total_customers) * 100) / 100
                    : 0,
                  color: 'text-blue-600',
                },
                {
                  label: 'Average Order Value',
                  val: stats?.orders_this_month > 0
                    ? formatKSH((stats?.revenue_this_month ?? 0) / stats.orders_this_month)
                    : formatKSH(0),
                  color: 'text-green-600',
                  small: true,
                },
                {
                  label: 'Contact Rate',
                  val: `${stats?.total_customers > 0
                    ? Math.round((1 - (stats?.customers_need_followup ?? 0) / stats.total_customers) * 100)
                    : 0}%`,
                  color: 'text-purple-600',
                },
                {
                  label: 'Order Rate',
                  val: `${stats?.total_customers > 0
                    ? Math.round((1 - (stats?.customers_no_recent_orders ?? 0) / stats.total_customers) * 100)
                    : 0}%`,
                  color: 'text-orange-600',
                },
              ].map((m, i) => (
                <div key={i} className="card">
                  <div className="card-body text-center py-5">
                    <div className={`text-2xl font-bold ${m.color} ${m.small ? 'text-lg break-words' : ''}`}>{m.val}</div>
                    <div className="text-sm text-gray-600 mt-1">{m.label}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Performance Analysis</h3>
              </div>
              <div className="card-body space-y-4">
                <div className="p-4 bg-green-50 border border-green-100 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">Strengths</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• {stats?.interactions_this_month ?? 0} customer interactions this month</li>
                    <li>• {stats?.orders_this_month ?? 0} orders generated</li>
                    <li>• {formatKSH(stats?.revenue_this_month ?? 0)} in monthly revenue</li>
                  </ul>
                </div>
                <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-2">Areas for Improvement</h4>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• {stats?.customers_need_followup ?? 0} customers need follow-up contact</li>
                    <li>• {stats?.customers_no_recent_orders ?? 0} customers haven't ordered recently</li>
                    <li>• Focus on converting interactions to orders</li>
                  </ul>
                </div>
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">Recommendations</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Schedule follow-up calls for overdue customers</li>
                    <li>• Create targeted campaigns for inactive customers</li>
                    <li>• Implement regular check-in schedules</li>
                    <li>• Track conversion rates from interactions to orders</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print footer */}
      <div className="hidden print:block mt-8 pt-4 border-t border-gray-200 text-center text-sm text-gray-500">
        Healthmax Limited – Customer Report System – Generated on {new Date().toLocaleDateString()}
      </div>
    </div>
  );
};

export default Reports;
